/**
 * THE SKY — A SKYLIGHT IS A HEMISPHERE LIGHT THAT KNOWS ABOUT OCCLUSION, AND THE ONLY WAY TO GET
 * ONE ON THIS STACK IS TO MAKE THE SKY A REAL EMITTER IN THE SCENE.
 *
 * ── Why this file exists ────────────────────────────────────────────────────────────────────────
 *
 * `src/render/indirect.ts` states the law: a constant `HemisphereLight` and an irradiance volume
 * are two implementations of ONE responsibility, and running both double-counts. That file settled
 * *who owns the term*. It could not settle *what the term is made of*, and G3.3 measured why:
 *
 *   - `scene.background` is a flat `0x1a2836` — linear luminance ~0.019 — and `scene.environment`
 *     is never set. The bake renders the scene through a `CubeCamera` per capture, so
 *     **our probes bake a near-black sky.** Every probe in the volume sees the same near-black
 *     everywhere, so there is no contrast between "open to the sky" and "under a stone arcade" for
 *     the volume to carry. The result is a position-varying warm wash — an ambient, not a skylight.
 *   - (C) three's own `examples/webgpu_lightprobes_sponza.html` (dev branch, retrieved 2026-08-31)
 *     — the official demo of the module we vendored — does `sky = new SkyMesh(); sky.scale
 *     .setScalar( 450000 );` and adds it TO THE SCENE, precisely so the bake's cube cameras
 *     photograph it. Its probes in the open court see a bright sky; its probes under the arcade
 *     see stone. **That difference is the darkening**, and it is the entire mechanism.
 *
 * So the sky here is GEOMETRY, and that is the load-bearing word. It is not a third implementation
 * of the indirect term: `HemisphereLight` and `SparseProbeVolume` both ADD a shading term at
 * shading time, and adding two of those is the double-count. A `SkyMesh` adds no shading term to anything.
 * It is a surface that emits, exactly like a bright wall, and it reaches surfaces through precisely
 * one path — the probe volume that photographs it. `applyIndirect()` remains the only writer of
 * `hemi.intensity`, and the volume remains the only carrier of the indirect term.
 *
 * ⚠️ `scene.environment` WOULD be a third implementation, and it is deliberately NOT used here. A
 * PMREM environment map applies `envNode` irradiance to every material directly, in parallel with
 * both of the above and invisible to `resolveIndirect`. See the report; that decision is the
 * owner's, not this file's.
 *
 * ── The five traps, each of which silently produces "the sky is there and does nothing" ─────────
 *
 * 1. **THE FAR PLANE.** The reference demo scales its sky to 450 000 because its camera reaches
 *    that far. Ours does not: `PerspectiveCamera(…, 0.1, 400)`, and — much worse — the probe
 *    bake's `CubeCamera` far plane must be passed EXPLICITLY (`CaptureBakeOptions.far`, which has
 *    no default for exactly this reason; the vendored v1 options defaulted it to 100 m). A sky at
 *    450 000 is clipped by both, renders nothing, and the bake photographs the same near-black it
 *    always did, with no error anywhere. {@link SKY_SCALE_MAX_M} is derived from the main camera's
 *    far plane, `createSky` clamps to it, and `scene.ts` passes an explicit bake `far` that
 *    contains the dome ({@link bakeFarForSky}).
 *
 * 2. **FOG.** `scene.fog = Fog(0x1a2836, 60, 150)` and `NodeMaterial.fog` defaults to `true`, so a
 *    dome 200 m away is 100 % fog colour — i.e. exactly the flat `0x1a2836` we are replacing,
 *    rendered through a more expensive shader. `material.fog = false` is mandatory, not a taste.
 *
 * 3. **THE VALIDITY CAPTURE.** Our fork of the bake renders a facing pass to measure the fraction
 *    of each probe's sphere that is BACKFACE, and marks probes that are mostly backface as invalid
 *    (i.e. buried in geometry). A `SkyMesh` is a `BackSide` box seen from inside: every one of its
 *    visible triangles is a backface. Left in that pass, **the probes that see the most sky are
 *    the ones marked most invalid** — the acceptance test inverted, silently. The fork already
 *    nulls `scene.background` for this pass, with the comment *"a ray that hits nothing keeps the
 *    cleared 0 and counts as valid, which is correct (open sky is not geometry)"*. A sky mesh is
 *    that same open sky, promoted to geometry, so it is hidden for that pass by the same rule.
 *    {@link SKY_MESH} is the marker `probeGrid.ts` looks for.
 *
 * 4. **THE SUN DISC IS THE SUN, A SECOND TIME — and this one is a LOTTERY, not a bias.** `SkyMesh`
 *    paints a solar disc of radiance `vSunE·19000·Fex·0.04` ~ **3 × 10^5**, against a sky of ~0.15
 *    around it. A probe bake integrates the whole sphere, so in principle that disc lands in the
 *    atlas AS THE SUN, next to the `DirectionalLight` that already is the sun — the
 *    hemisphere-plus-volume error (`src/render/indirect.ts`) with a third light type. (C) three's
 *    own `SkyMesh` documentation raises exactly this, and it is the only note of its kind in the
 *    file: *"It can be useful to hide the sun disc when generating an environment map to avoid
 *    artifacts — `sky.showSunDisc.value = false;`"*. A probe bake IS environment-map generation.
 *
 *    ⚠️ **MEASURED, AND THE MEASUREMENT CONTRADICTED THE OBVIOUS EXPECTATION — read this before
 *    trusting the fence.** On Sponza, 2026-08-31 (`tests/e2e/gi.e2e.ts` → G4, arm `DISC`), baking
 *    WITH the disc and WITHOUT it produced **identical** unobstructed sky irradiance — 3.9467 both,
 *    to four decimals. The disc is not a systematic term at our bake settings, and the arithmetic
 *    says why. It subtends 1.07 deg (`sunAngularDiameterCos = 0.9999566769`); a 32^2 cube-face
 *    texel subtends ~2.8 deg; the SH projection then samples 256 Fibonacci directions:
 *
 *      P(a texel centre falls inside the disc) ~ 3.6 %  x  P(a sample lands in that texel) ~ 4.9 %
 *      => ~0.2 % of probes, each of which then gets ~1.5 x 10^4 of irradiance against a true ~4.
 *
 *    So it is a one-in-five-hundred chance of a probe being wrong by three orders of magnitude,
 *    not a shift anyone can see in a mean — and it becomes systematic the moment someone raises
 *    `cubemapSize`, at which point the disc resolves and the double count is real.
 *
 *    ⚠️⚠️ **AND THAT IDENTICAL RESULT HAS A SECOND READING WHICH WAS NOT RULED OUT.** "The disc is
 *    sub-texel" and "`showSunDisc` never reached the shader" predict exactly the same number, and
 *    the second is a shape this codebase has hit before — `docs/ai/tsl-patterns.ts` pattern 5: a
 *    `uniform()` with no group lands in `objectGroup` and can keep rendering with the value its
 *    buffer was BUILT with. `SkyMesh`'s uniforms are ungrouped `uniform()` calls. The dome is a
 *    single render object, which is why `objectGroup` *should* be correct here — but "should" is
 *    the word this project has been burned by, and discriminating the two readings needs an
 *    instrument that can see a 15-pixel disc (a max-pixel or histogram reader; ours are means).
 *    **Not built, not measured, written down.**
 *
 *    The bracket is therefore kept and **honestly labelled unproven by measurement**: it guards a
 *    rare catastrophic outlier and a future settings change, on three's own advice and on the
 *    arithmetic above, and its measured effect today is zero for one of two reasons we did not
 *    separate. What IS asserted is that the bracket RESTORES the disc afterwards
 *    (`SkyInfo.sunDiscVisible`, checked after every bake in G4, and demonstrated failing by
 *    deleting the restore line) — a one-way failure there would silently delete the sun from the
 *    sky for the rest of the session.
 *
 * 5. **CLOUDS ARE ANIMATED.** `SkyMesh.cloudCoverage` defaults to 0.4 and the cloud layer is
 *    driven by the TSL `time` node, so the background — and therefore every probe bake and every
 *    screenshot — becomes a function of wall-clock time. Our default is 0: a measurement rig whose
 *    input drifts between the bake and the frame is not a measurement rig. Worlds may turn them on.
 *
 * ── Hard rule 7 (materials come from the factory) — the judgement, stated ───────────────────────
 *
 * `SkyMesh` builds its own `NodeMaterial` in its constructor and there is no way to hand it one.
 * That is allowed here, and the reasoning is the rule's own: the fence exists because "a fresh TSL
 * graph per material forks a full shader compile per material (`customProgramCacheKey` hashes node
 * ids)". A scene has **exactly one** sky — `createSky` is called once per `createScene` and the
 * mesh is a singleton — so the failure the rule prevents (N materials, N programs) cannot occur; a
 * sky dome is also not a *surface archetype*, which is what `surfaceFactory` vends. It is addon
 * output in the same sense GLTF-loaded materials are loader output, and the factory scan
 * (`tests/materialFactory.test.ts`) is untouched because nothing here constructs a
 * `MeshStandard/PhysicalNodeMaterial`. Flagged in the report rather than assumed.
 */
import { SkyMesh } from 'three/addons/objects/SkyMesh.js';
import type * as THREE from 'three/webgpu';
import type { WorldSkyConfig, WorldSunConfig } from '../world/schema';
import { sunPositionFromAngles } from './sunAngles';
// The clamp below is a VALUE use, and `export … from` does not bind into local scope.
import { CAMERA_FAR_M, SKY_SCALE_MAX_M } from './skyLimits.ts';

/**
 * `userData` key marking the scene's sky dome. Read by the vendored bake (`render/gi/probeGrid.ts`,
 * `FORK(fronterra)`) so the facing/validity capture can exclude it — trap 3 above. One definition,
 * imported by both sides, because two string literals is how that fork rots.
 */
export const SKY_MESH = 'fwgSkyMesh';

/** True for an object that is the scene's sky dome. Narrow, so callers can traverse a scene. */
export function isSkyMesh(o: { userData?: Record<string, unknown> }): boolean {
  return o.userData?.[SKY_MESH] === true;
}

/*
 * ── `CAMERA_FAR_M` / `SKY_SCALE_MAX_M` / `bakeFarForSky` LIVE IN `render/skyLimits.ts` NOW ───────
 *
 * Re-exported here unchanged so every existing import site keeps working — the SAME move
 * `sunPositionFromAngles` made at the bottom of this file, for the same reason and with the same
 * "one definition, two doors" result. They moved because the traced baker resolves the atlas
 * header's `lighting` block from `world.json` in NODE, and `sky.scaleM` in that block is the
 * CLAMPED scale: `tools/` therefore needs `SKY_SCALE_MAX_M`, and this file is unreachable from
 * Node (it value-imports `three/webgpu` and spells its own relative imports without a `.ts`
 * extension — MEASURED: `ERR_MODULE_NOT_FOUND` on `./sunAngles`).
 */
export { BAKE_FAR_NO_SKY_M, CAMERA_FAR_M, SKY_SCALE_MAX_M, bakeFarForSky } from './skyLimits.ts';

/*
 * ⚠️ THE DOME IS CAMERA-LOCKED NOW, AND THAT CHANGES WHAT THIS CEILING MEANS.
 *
 * `scene.ts` copies the camera's position onto the mesh every frame, so the camera is always at the
 * dome's centre and the dome can never intersect world geometry. Before that it sat at the origin,
 * which on a level larger than the dome (Mars is 688 m across against a 400 m dome) meant the sky
 * INTERSECTED THE LEVEL — a blue surface clipping through the vista, which is exactly how the owner
 * described it.
 *
 * So this clamp no longer protects against "the dome is smaller than the world". It protects
 * against one thing only: the far corner of a `BackSide` box at `scale · √3 / 2` must stay inside
 * the far plane, or the sky renders with holes. It is a VIEW-DISTANCE ceiling, not a level-size one,
 * and it moves when `CAMERA_FAR_M` does.
 */

/*
 * ── `sunPositionFromAngles` / `anglesFromSunPosition` LIVE IN `render/sunAngles.ts` NOW (B2) ────
 *
 * Re-exported here unchanged so every existing import site keeps working. They moved because the
 * editor's PURE placement module has to aim a dropped spot light from a surface normal, and a pure
 * file may not sit on a `three` importer's runtime chain (this file value-imports `three/webgpu`).
 * `sunAngles.ts`'s header carries the argument; this is still "the one converter" — one definition,
 * two doors.
 */
export { anglesFromSunPosition, sunPositionFromAngles } from './sunAngles';

/**
 * The sun's IRRADIANCE ON A HORIZONTAL SURFACE, in the same units as the light's `intensity`.
 *
 * three multiplies nothing else in: `AnalyticLightNode.update()` does
 * `this.color.copy(light.color).multiplyScalar(light.intensity)` (r185) and the standard BRDF then
 * applies `dotNL · lightColor · albedo/π`. So for a horizontal surface the incident irradiance is
 * `intensity · sin(elevation)` — the only quantity that can honestly be compared against the sky's
 * hemispherical irradiance, and the basis for the sun-to-sky ratio the world data records.
 */
export function sunHorizontalIrradiance(intensity: number, elevationDeg: number): number {
  return intensity * Math.max(0, Math.sin((elevationDeg * Math.PI) / 180));
}

/** The live sky's self-report — published through the dev hook so a run can assert on it. */
export interface SkyInfo {
  /** Is a sky dome in the scene right now? */
  readonly on: boolean;
  /** Uniform scale of the dome in metres (its half-extent is half this). */
  readonly scaleM: number;
  /** Preetham parameters actually on the uniforms — read back off the mesh, never off the config. */
  readonly turbidity: number;
  readonly rayleigh: number;
  readonly mieCoefficient: number;
  readonly mieDirectionalG: number;
  readonly cloudCoverage: number;
  /**
   * Is the solar disc currently painted into the dome? It must be **false while a probe bake runs**
   * (trap 4 above — the disc is the sun a second time) and true otherwise. Read off the uniform, so
   * a bracket that failed to restore it is visible rather than merely unlikely.
   */
  readonly sunDiscVisible: boolean;
  /** The sun position the dome's `sunPosition` uniform holds, normalized. */
  readonly sunDir: [number, number, number];
}

/** What `createSky` hands back — the mesh plus the three things the caller must be able to do. */
export interface SkyHandle {
  readonly mesh: THREE.Object3D;
  /**
   * Re-point the dome at the sun. Called from `GameScene.applyWorldLights` whenever the sun moves.
   *
   * ⚠️ B1: this had NO caller outside its own constructor for its whole life — the doc line said
   * "called whenever the sun moves" and nothing ever moved the sun. `tests/editorResolvedRig.test.ts`
   * scans for a caller in `scene.ts` so the claim cannot rot back into a wish.
   */
  setSunPosition(pos: readonly [number, number, number]): void;
  /**
   * B1 · Write an edited sky block onto the LIVE dome — the six Preetham uniforms and the scale —
   * so a `lighting.sky` edit in the editor reaches the frame the way a `lights[]` edit does.
   * `enabled` is NOT handled here: a dome that exists is on, and the caller (`applyWorldLights`)
   * owns construction/removal because it also owns "who put the sky in the scene". Every value
   * written here is read back by `info()`, so `liveLighting()` and the `sky` provenance arm follow
   * the frame and an edited sky makes the atlas honestly OUT OF DATE at the next load.
   */
  apply(cfg: WorldSkyConfig): void;
  /**
   * Show or hide the solar disc. **Hidden for the duration of a probe bake** and restored in the
   * bake bracket's `finally` — trap 4: the disc is the `DirectionalLight` a second time, and it is
   * 89 % of the "sky" the atlas records if left in.
   */
  setSunDiscVisible(on: boolean): void;
  /** Read the uniforms BACK OFF THE MESH — never off the config that was asked for. */
  info(): SkyInfo;
  dispose(): void;
}

/**
 * Build the scene's sky dome from world data, or return `null` when the world does not want one.
 *
 * ⚠️ NOTHING HERE ADDS THE MESH TO A SCENE. The caller owns that, because the caller also owns the
 * bake bracket that has to see it — and "who put the sky in the scene" is exactly the sort of
 * question that must have one answer.
 */
export function createSky(
  cfg: WorldSkyConfig,
  sunPos: readonly [number, number, number],
): SkyHandle | null {
  if (!cfg.enabled) return null;

  const sky = new SkyMesh();
  /** Rule 6: the clamp warns ONCE per dome, not once per slider pixel an artist drags past it. */
  let clampWarned = false;
  /**
   * Write a config's six uniforms and scale onto the mesh. The ONE writer, used at construction
   * and by `apply()` (B1), so an editor edit and a boot read the same code path — two writers is
   * how a slider comes to set a uniform the constructor never did, or the reverse.
   */
  const write = (c: WorldSkyConfig): void => {
    const scaleM = Math.min(c.scaleM, SKY_SCALE_MAX_M);
    if (c.scaleM > SKY_SCALE_MAX_M && !clampWarned) {
      clampWarned = true;
      // Constitution rule 6: warn once, never throw. A clipped dome is a sky with holes in it, and
      // the failure is invisible to every state assertion — it is only ever a picture.
      console.warn(
        `[sky] scaleM ${c.scaleM} exceeds the camera's far plane (max ${SKY_SCALE_MAX_M.toFixed(1)} ` +
          `for far=${CAMERA_FAR_M}); clamped to ${scaleM.toFixed(1)}. A dome past the far plane ` +
          `renders with clipped corners and bakes as black sky.`,
      );
    }
    sky.scale.setScalar(scaleM);
    sky.turbidity.value = c.turbidity;
    sky.rayleigh.value = c.rayleigh;
    sky.mieCoefficient.value = c.mieCoefficient;
    sky.mieDirectionalG.value = c.mieDirectionalG;
    sky.cloudCoverage.value = c.cloudCoverage;
    // Matrix policy (plan §4.1): the project boots with DEFAULT_MATRIX_AUTO_UPDATE = false, so a
    // posed-once object must call updateMatrix() itself or its scale never reaches matrixWorld —
    // which would leave a 1 m cube around the origin, inside every wall, lighting nothing.
    sky.updateMatrix();
  };
  write(cfg);
  const mat = sky.material as THREE.Material & { fog: boolean; allowOverride: boolean };
  // TRAP 2. Without this the dome is 100 % fog colour at any useful scale — the flat background we
  // are replacing, drawn through an atmospheric scattering shader.
  mat.fog = false;
  /*
   * B1: opted out of `scene.overrideMaterial` HERE as well as by `debugView`'s sweep. The sweep
   * runs on view-mode ENTRY, and the editor can now construct a dome (Sky dome on) while a view
   * mode is already active — a dome created after the sweep would be repainted a false colour in
   * Overdraw or Indirection Level. The predicate `isSkyMesh` is still the debug view's; this is
   * the property being true at birth rather than a second definition of it.
   */
  mat.allowOverride = false;
  // TRAP 3. The bake's facing/validity pass must not see the dome, or every probe that can see sky
  // reads as buried in geometry. `probeGrid.ts` looks for exactly this key.
  sky.userData[SKY_MESH] = true;

  const setSunPosition = (pos: readonly [number, number, number]): void => {
    sky.sunPosition.value.set(pos[0], pos[1], pos[2]);
  };
  setSunPosition(sunPos);

  return {
    mesh: sky as unknown as THREE.Object3D,
    setSunPosition,
    apply: write,
    setSunDiscVisible(on: boolean): void {
      sky.showSunDisc.value = on ? 1 : 0;
    },
    info(): SkyInfo {
      // READ BACK OFF THE MESH. `cfg` is what was asked for; these are what the uniforms hold and
      // what the shader copies. Same discipline as `indirect()` reading `hemi.intensity`.
      const d = sky.sunPosition.value;
      const len = Math.hypot(d.x, d.y, d.z) || 1;
      return {
        on: true,
        scaleM: sky.scale.x,
        turbidity: sky.turbidity.value,
        rayleigh: sky.rayleigh.value,
        mieCoefficient: sky.mieCoefficient.value,
        mieDirectionalG: sky.mieDirectionalG.value,
        cloudCoverage: sky.cloudCoverage.value,
        sunDiscVisible: sky.showSunDisc.value !== 0,
        sunDir: [d.x / len, d.y / len, d.z / len],
      };
    },
    dispose(): void {
      sky.geometry.dispose();
      (sky.material as THREE.Material).dispose();
    },
  };
}

/** The reported state when a world has no sky — so the snapshot field is never `null`-shaped. */
export const SKY_OFF: SkyInfo = {
  on: false,
  scaleM: 0,
  turbidity: 0,
  rayleigh: 0,
  mieCoefficient: 0,
  mieDirectionalG: 0,
  cloudCoverage: 0,
  sunDiscVisible: false,
  sunDir: [0, 1, 0],
};

/**
 * Sun position for a world's sun config — the one place the angles become a vector, so the shadow
 * camera, the light and the sky dome's `sunPosition` uniform can never disagree.
 */
export function sunPositionOf(sun: WorldSunConfig): [number, number, number] {
  return sunPositionFromAngles(sun.azimuthDeg, sun.elevationDeg, sun.distanceM);
}
